import React, {
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  useQuery,
  useMutation,
  queryCache,
} from 'react-query';
import Page from '../components/Page';
import HeaderTitle from '../components/HeaderTitle';
import Container from '../components/Container';
import Loader from './../components/Loader';
import Title from '../components/Title';
import Paragraph from '../components/Paragraph';
import Hr from '../components/Hr';
import Button from '../components/Button';
import RadioButton from '../components/RadioButton';
import TicketCodeInput from '../components/TicketCodeInput';
import Switcher from '../components/Switcher';
import Pass from '../components/Pass';
import SquarePasses from '../components/SquarePasses';
import ErrorMessages from './../components/ErrorMessages';
import GuestModal from '../modals/Guest';
import Alert from '../modals/Alert';
import requsetErrorMessage from './../libraries/requsetErrorMessage';
import api from '../api';
import squaresIcon from '../assets/images/icons/Square_List.svg';
import listIcon from '../assets/images/icons/List_Black.svg';
import PlusIcon from '../assets/images/icons/Plus.svg';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { MdReply } from 'react-icons/md';
import ParkingShareModal from './ParkingShareModal';
import parkingTicketIcon from '../assets/images/sidemenu-icons/Ticket_white.svg';
import formatPin from '../libraries/formatPin';

// Season rows put the person the ticket was forwarded to in the status slot and
// leave the used state to the tick beside it. Parking said "Bestätigt" there
// instead and never named the recipient, which read as two different products.
const recipientName = card =>
  [card.recipient_first_name, card.recipient_last_name].filter(Boolean).join(' ');

/**
 * Minimum width to SquarePasses been shown
 */
const minSquarePassesWidth = 1300;

/**
 * Guest modal public fields
 * @type {[string]}
 */
const publicFields = ['first_name', 'last_name', 'phone'];

/**
 * Blue "P" badge that marks a row/column as a Parkkarte (mirrors the app).
 * The same badge is used in the list and the matrix, only larger in the
 * column header where the season passes show their club logo.
 * @param {Object} [props]
 * @param {number} [props.size] - Edge length in px
 * @component
 */
const ParkingBadge = ({ size = 24, marginRight = 8, className }) => (
  <span
    className={className}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      minWidth: size,
      borderRadius: Math.round(size / 5),
      background: '#1f6fe0',
      color: '#fff',
      fontWeight: 'bold',
      fontSize: Math.round(size * 0.62),
      marginRight,
      flex: 'none',
    }}
  >
    P
  </span>
);

/**
 * Tickets page
 * @component
 */
export default () => {
  const [visibleSwitcher, setVisibleSwitcher] = useState(window.innerWidth >= minSquarePassesWidth);
  const [ticketCode, setTicketCode] = useState('');
  const [guestData, setGuestData] = useState(null);
  const [hideSide, setHideSide] = useState(true);
  const [errors, setErrors] = useState(null);
  const [IsErrors, setIsErrors] = useState(null);
  const [lotteryRequest, setLotteryRequest] = useState(null);
  const [leagueAcronym, setLeagueAcronym] = useLocalStorageState('leagueAcronym', "NL");
  const [listView, setListView] = useLocalStorageState('listView', "list");

  // Parking card currently open in the "Weiterleiten" (forward/share) modal.
  const [shareCard, setShareCard] = useState(null);

  const ticketCodeInput = useRef();
  const { data, error, isLoading, isError } = useQuery( ['passes', leagueAcronym],() => api.pass.passes({ league_acronym: leagueAcronym }));
  const { data: contacts } = useQuery('contacts', api.contact.contacts);
  // Parkkarten are merged into this Abos calendar (men's home games only).
  const { data: parkingData } = useQuery('parking-me', api.parking.me);
  // Season and parking codes look identical, so this goes to the unified
  // endpoint, which resolves which kind it is. Parking cards render as extra
  // columns in this same matrix, so both kinds just refresh their own query.
  const [assign, { error: assignError, isLoading: isAssignLoading }] = useMutation(api.pass.activate, {
    onSuccess({ data }) {
      if (data?.type === 'parking') {
        queryCache.invalidateQueries('parking-me');
      } else if (leagueAcronym !== 'NL') {
        setLeagueAcronym('NL');
      } else {
        queryCache.invalidateQueries(['passes', 'NL']);
      }

      ticketCodeInput.current.clear();

      setHideSide(true);
    },
  });
  const [access, { error: accessError, isLoading: isAccessLoading }] = useMutation(api.access.access, {
    onSuccess({ data: access }, { game_id, pass_id }) {
      queryCache.setQueryData(['passes', leagueAcronym], queryCacheData => ({
        ...queryCacheData,
        data: queryCacheData.data.map(qd => {
          if (qd.id !== game_id) {
            return qd;
          } else {
            return qd.passes.find(pass => pass.id === pass_id)
              ? { ...qd, passes: qd.passes.map(pass => pass.id === pass_id ? { ...pass, access } : pass) }
              : qd
          }
        }),
      }));
    },
  });
  const [accesses, { error: accessesError, isLoading: isAccessesLoading }] = useMutation(api.access.accesses, {
    onSuccess(data) {
      queryCache.setQueryData(['passes', leagueAcronym], data);
    },
  });
  const [accessRequest, { error: accessRequestError, isLoading: isAccessRequestLoading, reset: accessRequestReset }] = useMutation(api.access.accessRequest, {
    onSuccess() {
      queryCache.setQueryData(['passes', leagueAcronym], queryCacheData => ({
        ...queryCacheData,
        data: queryCacheData.data.map(qd => {
          if (qd.id !== lotteryRequest.gameId) {
            return qd;
          } else {
            return qd.passes.find(pass => pass.id === lotteryRequest.passId)
              ? { ...qd, passes: qd.passes.map(pass => pass.id === lotteryRequest.passId ? { ...pass, request_status: 'applied' } : pass) }
              : qd
          }
        }),
      }));

      setLotteryRequest(null);
    },
  });
  const [createNonExistentContact, { isLoading: isCreateNonExistentContactLoading }] = useMutation(api.contact.createNonExistentContact, {
    onSuccess(data) {
      if (!data) return;

      queryCache.setQueryData('contacts', queryCacheData => !queryCacheData ? queryCacheData : ({
        ...queryCacheData,
        data: [
          ...queryCacheData.data,
          data.data,
      ]}));
    },
  });

  useEffect(() => {
    if (data && data.data && passesIds().length > 5) {
      setListView('list');

      setVisibleSwitcher(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const resize = () => {
      if (window.innerWidth < minSquarePassesWidth) {
        setListView('list');

        setVisibleSwitcher(false);
      } else {
        setVisibleSwitcher(true);
      }
    };

    resize();

    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  // Reset errors on Assigning ticket
  useEffect(() => {
    setIsErrors(!!accessError?.data?.message || !!accessesError?.data?.message);

    setErrors(!!accessError?.data?.message ? accessError.data.message : (!!accessesError?.data?.message ? accessesError.data.message : ''));
  }, [accessError, accessesError]);

  useEffect(() => {
    setIsErrors(null);

    setErrors(null);
  }, [guestData]);

  /**
   * Invite link with token
   * @param {string} token - Anonymous token
   * @returns {string}
   */
  const inviteLink = token => `${window.location.origin}/invite/${token}`;

  /**
   * Find all ID(s) from pass(es)
   * @returns {[number]}
   */
  const passesIds = () => {
    let ids = [];

    data.data.forEach(game => game.passes.forEach(pass => ids.push(pass.id)));

    // Unique ID(s)
    return [...new Set(ids)];
  }

  /**
   * Find all passes with pass details
   * @returns {[object]}
   */
  const getAllPasses = () => {
    let passes = [];

    data.data.forEach(game => game.passes.forEach(pass => {if (!passes.find(p => p.id === pass.id)) {
      passes.push({id: pass.id, token: pass.assign_token, type: pass.type, seat: pass.seat})
    }}));

    return passes;
  }

  /**
   * Find access
   * @param {number} gameId - ID of game
   * @returns {string}
   */
  const accessByGame = gameId => {
    const { passes } = data.data.find(game => game.id === gameId) || {};

    return passesIds().map(passId => Array.isArray(passes) ? passes.find(pass => pass.id === passId)?.access : undefined);
  };

  /**
   * Has anonymous link
   * @param {number} gameId - ID of game
   * @param {number} passId - ID of pass
   * @returns {boolean}
   */
  const hasAnonymousLinkByIds = (gameId, passId) => {
    // Parking-only games are rendered alongside the season ones but are absent
    // from /me/passes, so there is no pass list to look through.
    const { passes = [] } = data.data.find(game => game.id === gameId) || {};

    const pass = passes.find(pass => pass.id === passId) || {};

    return !!pass.create_anonymous_links;
  };

  /**
   * Findout is game is finished
   * @param {number} gameId - ID of game
   * @returns {boolean}
   */
  const isGameOver = gameId => (!!data.data.find(game => game.id === gameId)?.hasEnded || false);

  /**
   * Findout is game has a date and time
   * @param {number} gameId - ID of game
   * @returns {boolean}
   */
  const hasGameDate = gameId => !!data.data.find(game => game.id === gameId)?.time;

  /**
   * Find request status for given pass ID
   * @param {number} passId - ID of pass
   * @param {number} gameId - ID of game
   * @returns {string}
   */
  const requestStatus = (passId, gameId) => data.data.find(game => game.id === gameId)?.passes.find(pass => pass.id === passId)?.request_status || null;

  /**
   * Find draw date and time
   * @param {number} gameId - ID of game
   * @returns {string | null}
   */
  const drawDate = gameId => data.data.find(game => game.id === gameId)?.draw_date || null;

  const accessComponent = gameId => (access, index) => {
    const passId = passesIds()[index];
    const hasAnonymousLink = hasAnonymousLinkByIds(gameId, passId);

    if (!!access) {
      return access.anonymous_token ? (
        <SquarePasses.Invite
          key={index}
          onInviteClick={() => setGuestData({
            game_id: gameId,
            pass_id: passId,
            has_anonymous_link: hasAnonymousLink,
            anonymous_link: access.anonymous_token ? inviteLink(access.anonymous_token) : '',
          })}
        />
      ) : (
        <SquarePasses.User
          key={index}
          name={`${access.first_name || ''} ${access.last_name || ''}`}
          phone={access.phone}
          token={access.access_token}
          scanned={!!access.scanned_at}
          inactive={hasGameDate(gameId) ? isGameOver(gameId) : false}
          editHide={!hasGameDate(gameId)}
          onEditClick={() => {
            setGuestData({
              game_id: gameId,
              pass_id: passId,
              has_anonymous_link: hasAnonymousLink,
              anonymous_link: access.anonymous_token ? inviteLink(access.anonymous_token) : '',
            })}}
        />
      );
    } else if (access === null) {
      const status = requestStatus(passId, gameId);

      return status ? (
        <SquarePasses.Lottery
          key={index}
          status={status}
          dateTime={drawDate(gameId) || ''}
          disabled={isGameOver(gameId) || !hasGameDate(gameId)}
          onClick={() => {
            if (status === 'needs_grant') {
              setLotteryRequest({ gameId, passId });
            } else if (status === 'granted') {
              setGuestData({
                game_id: gameId,
                pass_id: passId,
                has_anonymous_link: hasAnonymousLink,
                anonymous_link: '',
              });
            }
          }}
        />
      ) : (
        <SquarePasses.Edit
          key={index}
          disabled={isGameOver(gameId) || !hasGameDate(gameId)}
          onClick={() => {
              setGuestData({
              game_id: gameId,
              pass_id: passId,
              has_anonymous_link: hasAnonymousLink,
              anonymous_link: '',
            })}}
        />
      );
    } else if (access === undefined) {
      return <SquarePasses.NoMatch key={index} />;
    }
  };

  const findGuestData = params => {
    if (typeof params !== 'object' || !params.game_id || !params.pass_id) {
      return {};
    }

    return data.data
      .find(game => game.id === params.game_id)
      .passes
      .find(pass => pass.id === params.pass_id)
      .access;
  };

  const handleGuestModalConfirm = userData => {
    const withGameId = (userData.assign_pass || !guestData.game_id) ? false : true

    userData.assign_pass && delete userData.assign_pass

    const guestUserData = findGuestData(guestData);

    const guestId = guestData ? guestUserData?.id : null;

    const promise = withGameId
      ? access({
        // user's data
        ...(() => {
          if (!guestUserData?.last_viewed_at) {
            return userData;
          }

          if (guestUserData?.phone !== userData?.phone) {
            return userData;
          }

          return Object.keys(userData).reduce((accumulator, key) => ({
            ...accumulator,
            ...publicFields.indexOf(key) >= 0
              ? { [key]: userData[key] }
              : (userData[key] !== null && userData[key] !== guestUserData[key] ? { [key]: userData[key] } : {}),
          }), {});
        })(),
        ...guestId ? { id: guestId } : {},
        game_id: guestData.game_id,
        pass_id: guestData.pass_id,
      })
      : accesses({ ...userData, pass_id: guestData.pass_id });

    Promise.allSettled([promise, createNonExistentContact(userData)])
      .catch(console.warn)
      .finally(() => setGuestData(null));
  };

  // TODO: handle update of all games from one response
  const handleCreateLink = ({ data: { anonymous_token } }) => {
    const { game_id, pass_id } = guestData;

    queryCache.setQueryData(['passes', leagueAcronym], queryCacheData => ({
      ...queryCacheData,
      data: queryCacheData.data.map(qd => {
        if (qd.id !== game_id) {
          return qd;
        } else {
          return qd.passes.find(pass => pass.id === pass_id)
            ? { ...qd, passes: qd.passes.map(pass => pass.id === pass_id ? { ...pass, access: { ...pass.access, anonymous_token } } : pass) }
            : qd
        }
      }),
    }));

    setGuestData(data => ({
      ...data,
      has_anonymous_link: true,
      anonymous_link: inviteLink(anonymous_token),
    }));
  };

  const sordData = () => {
    let d = data?.data;

    if (!d) return d;

    d.forEach(el => {
      if (el.passes) {
        for (let i = 0; i < el.passes.length; i++) {
          const pass = el.passes[i];

          if (pass.seat) {
            pass.__sort__ = `${pass.seat.seat_group.sector.title} ${pass.seat.seat_group.title} ${pass.seat.row} ${pass.seat.num}`;
          }
        }
      }
    });

    d = d.map(row => ({
      ...row,
      passes: row.passes.sort((a, b) => a.__sort__ === [a.__sort__, b.__sort__].sort()[0] ? -1 : 1),
    }));

    return d;
  };

  const handleLotteryRequestClose = () => {
    accessRequestReset();

    setLotteryRequest(null);
  };

  // ---- Parkkarten merged into this calendar (men's home games only) ----
  // Parking is only offered for men's games; keep the FRAUEN tab parking-free.
  const parkingCards = (leagueAcronym === 'NL' && parkingData?.data) ? parkingData.data : [];
  const parkingPasses = [];
  const parkingSeen = {};
  const parkingCellByKey = {}; // `${game_id}:${parking_pass_id}` -> card
  const parkingByGame = {};
  parkingCards.forEach(c => {
    if (!parkingSeen[c.parking_pass_id]) {
      parkingSeen[c.parking_pass_id] = true;
      parkingPasses.push({
        id: c.parking_pass_id,
        field_name: c.field?.name,
        assign_token: c.assign_token,
      });
    }
    parkingCellByKey[`${c.game_id}:${c.parking_pass_id}`] = c;
    (parkingByGame[c.game_id] = parkingByGame[c.game_id] || []).push(c);
  });

  // ISO date strings → lexicographic compare is chronological.
  const byTime = (a, b) => String(a.time || '').localeCompare(String(b.time || ''));

  // Games that only have parking (no season pass row) still need to appear.
  const seasonGames = (data && data.data) ? data.data : [];
  const parkingOnlyGames = Object.keys(parkingByGame)
    .filter(gid => !seasonGames.some(g => String(g.id) === String(gid)))
    .map(gid => {
      const c = parkingByGame[gid][0];
      return {
        id: Number(gid),
        opponent: c.opponent,
        opponent_logo: c.opponent_logo,
        time: c.game_date,
        location: c.location,
        passes: [],
      };
    });

  // Matrix cell for one game + parking pass.
  const renderParkingCell = (gameId, p) => {
    const card = parkingCellByKey[`${gameId}:${p.id}`];

    if (!card) return <SquarePasses.NoMatch key={`pk-${p.id}`} />;

    const used = !!card.used_at;

    return (
      <div className="SquarePasses-Item user" key={`pk-${p.id}`}>
        <div className="SquarePasses-Item-body">
          {/* The lot is named in the column header, so the cell only carries
              who holds this particular card — as the season cell does. */}
          <h3 className="name">{recipientName(card)}</h3>
        </div>

        {used && (
          <div className="SquarePasses-Item-icon">
            <FaCheck size={24} />
          </div>
        )}

        <Link
          to={`/parking-eticket-me/${card.access_code_id}`}
          className={`SquarePasses-Item-ETicket ${used ? 'bottom' : ''}`}
          target="_blank"
        >
          <img src={parkingTicketIcon} alt="ETicket" style={{ width: 20, marginRight: 5 }} /> eTicket
        </Link>

        {/* Sharing stays available even after the card is used, to match the app.
            Uses the season footer class rather than a hand-rolled copy of it. */}
        <div className="SquarePasses-Item-footer" onClick={() => setShareCard(card)}>
          <span>
            <MdReply size={24} style={{ transform: 'scaleX(-1)', marginBottom: -6 }} /> Weiterleiten
          </span>
        </div>
      </div>
    );
  };

  // List-view rows for a game's parking cards (rendered under the season items).
  // The P badge sits in the 2nd column, which is the one kept visible on mobile
  // (1st/3rd columns are display:none below 991px). Sharing stays available even
  // after the card is used, to match the app.
  const renderParkingListItems = gameId => (parkingByGame[gameId] || []).map(card => {
    const used = !!card.used_at;

    return (
      <div className="Pass-Item" key={`pk-${card.access_code_id}`}>
        {/* Columns mirror the season-ticket row: Abo / Platz / Typ / Status. */}
        <div className="Pass-Item-details">
          <span className="Pass-Item-filed">{formatPin(card.assign_token)}</span>

          <span className="Pass-Item-filed">
            <span className="Pass-Item-parking-badge-mobile">
              <ParkingBadge />
            </span>
            {card.field?.name || ''}
          </span>

          <span className="Pass-Item-filed">
            <ParkingBadge /> Parkkarte
          </span>

          <span className="Pass-Item-name">{recipientName(card)}</span>
        </div>

        {used && (
          <div className="Pass-Item-scanned">
            <FaCheck size={24} />
          </div>
        )}

        <Link
          className="Pass-Item-ETicket"
          to={`/parking-eticket-me/${card.access_code_id}`}
          target="_blank"
        >
          <img src={parkingTicketIcon} alt="ETicket" />
        </Link>

        <div className="Pass-Item-edit" onClick={() => setShareCard(card)} title="Weiterleiten">
          <MdReply size={26} style={{ transform: 'scaleX(-1)' }} />
        </div>
      </div>
    );
  });

  return (
    <Page redirect>
      <HeaderTitle>MySCB</HeaderTitle>

      <Container>
        <Container.SideMenu refreshSide={() => setHideSide(true)}/>

        <Container.Side hideSide={hideSide}>
          <Title
            small
            theme="side"
          >
            Saisonabo(s)
          </Title>

          <Paragraph>
            Hier kannst du den Code deines Saisonabo eingeben, den du von uns per Brief zugestellt erhalten hast, um dein Saisonabo zu aktivieren.
          </Paragraph>
          <Paragraph>
            Gib den Code oder die Codes (falls du mehrere Saisonabos verwaltest) in den folgenden Feldern ein.
          </Paragraph>


          <Hr />

          <h3 style={{
            margin: '20px 0',
            padding: 0,
            color: 'white',
            fontSize: 16,
          }}>
            Code Saisonabo(s)
          </h3>

          <TicketCodeInput
            ref={ticketCodeInput}
            onChange={setTicketCode}
          />

          {!!assignError?.data?.message && <ErrorMessages messages={requsetErrorMessage(assignError.data.message)} />}

          <Button
            fullwidth
            margin="top"
            disabled={ticketCode.length !== 11}
            loading={isAssignLoading}
            onClick={() => assign({ assign_token: ticketCode })}
          >
            Saisonabo aktivieren
          </Button>

          <Hr />
        </Container.Side>

        <Title>
          Match und Saisonabo auswählen
        </Title>

        {!!error?.data?.message && <ErrorMessages messages={requsetErrorMessage(error.data.message)} />}

        {isLoading ? <Loader style={{ marginBottom: 30 }} /> : !isError && (
          <>
            <div className = "action-buttons">
              <Button
                onClick={() => setHideSide(!hideSide)}
                icon={{ src: PlusIcon, alt: 'icon' }}
                style={{ maxWidth: 260, textAlign: 'left', lineHeight: '26px' }}
              >
                Neues Saisonabo hinzufügen
              </Button>
      
              {visibleSwitcher && !!data && (
                <Switcher
                  align="right"
                >
                  <Switcher.Button
                    active={listView === 'squares'}
                    icon={squaresIcon}
                    onClick={() => setListView('squares')}
                  />

                  <Switcher.Button
                    active={listView === 'list'}
                    icon={listIcon}
                    onClick={() => setListView('list')}
                  />
                </Switcher>
              )}
            </div>
            <div className="radio-group">
              <RadioButton
                value="NL"
                label="MÄNNER"
                selected={leagueAcronym}
                onChange={setLeagueAcronym}
                radiusStyle="left"
              />
              <RadioButton
                value="PF WL"
                label="FRAUEN"
                selected={leagueAcronym}
                onChange={setLeagueAcronym}
                radiusStyle="right"
              />
            </div>

            {listView === 'squares' && data && (!!(data.data && data.data.length) || !!parkingOnlyGames.length) && (
              <SquarePasses>
                <SquarePasses.Row>
                  <SquarePasses.Games />

                  {data && data.data && getAllPasses().map(pass => (
                    <SquarePasses.Pass
                      key={pass.id}
                      assignToken={pass.token}
                      passType={pass.type}
                      passSeat={pass.seat}
                    />
                  ))}

                  {/* Parking columns after the season passes. Same shape as a
                      Saisonabo column: badge, title, activation code, then the
                      lot where the seat would be — so the lot name is named
                      once here instead of being repeated in every cell. */}
                  {parkingPasses.map(p => (
                    <div className="SquarePasses-Item" key={`pkh-${p.id}`}>
                      <div className="SquarePasses-Item-body">
                        <ParkingBadge size={32} marginRight={0} className="logo" />

                        <h3>Parkkarte</h3>

                        <span className="token">{p.assign_token || ''}</span>

                        <br />

                        <span className="type">{p.field_name}</span>
                      </div>
                    </div>
                  ))}
                </SquarePasses.Row>

                {[...seasonGames, ...parkingOnlyGames].sort(byTime).map(game => (
                  <SquarePasses.Row key={game.id}>
                    <SquarePasses.Club
                      clubLogo={game.opponent_logo}
                      clubName={game.opponent}
                      dateTime={game.time}
                    />

                    {accessByGame(game.id).map(accessComponent(game.id))}

                    {parkingPasses.map(p => renderParkingCell(game.id, p))}
                  </SquarePasses.Row>
                ))}
              </SquarePasses>
            )}

            {listView === 'list' && data && [...(sordData() || []), ...parkingOnlyGames].sort(byTime).map(game => hasGameDate(game.id) && isGameOver(game.id) ? null : (
              <Pass
                key={game.id}
                image={game.opponent_logo}
                club={game.opponent}
                dateTime={game.time}
                stadium={game.location}
                city="Bern"
              >
                {game.passes.map(pass => (
                  <Pass.Item
                    key={pass.id}
                    assignToken={pass.assign_token}
                    accessToken={pass.access?.access_token}
                    status={false}
                    drawDate={drawDate(game.id) || ''}
                    place={pass.seat ? `${pass.seat.seat_group.title} ${pass.seat.row ? `/ ${pass.seat.row}` : ''}  ${pass.seat.num ? `/ ${pass.seat.num}` : ''}` : ''}
                    name={pass.access ? `${pass.access.first_name || ''} ${pass.access.last_name || ''}` : ''}
                    // type={''}
                    scanned={pass.alreadyScanned}
                    inactive={hasGameDate(game.id) ? isGameOver(game.id) : false}
                    phone={pass.access?.phone || ''}
                    invite={!!pass.access?.anonymous_token}
                    editDisabled={!hasGameDate(game.id)}
                    onEditClick={pass.request_status === 'needs_grant' ? () => setLotteryRequest({ gameId: game.id, passId: pass.id })
                      : () => setGuestData({
                        game_id: game.id,
                        pass_id: pass.id,
                        has_anonymous_link: hasAnonymousLinkByIds(game.id, pass.id),
                        anonymous_link: pass.access?.anonymous_token ? inviteLink(pass.access.anonymous_token) : '',
                      })
                    }
                  />
                ))}

                {renderParkingListItems(game.id)}
              </Pass>
            ))}
          </>
        )}

        {!!guestData && (
          <GuestModal
            privateFields={guestData ? !!findGuestData(guestData)?.last_viewed_at : false}
            user={guestData ? findGuestData(guestData) : {}}
            anonymousLinkData={guestData.has_anonymous_link ? {
              gameId: guestData.game_id,
              passId: guestData.pass_id,
              link: guestData.anonymous_link,
              disabled: isGameOver(guestData.game_id),
            } : {}}
            contacts={contacts && contacts.data ? contacts.data : null}
            size="medium"
            isOpen={!!guestData}
            isLoading={isAccessLoading || isAccessesLoading || isCreateNonExistentContactLoading}
            title="Angaben des Matchbesuchers"
            onConfirm={handleGuestModalConfirm}
            onRequestClose={isAccessLoading || isAccessesLoading || isCreateNonExistentContactLoading ? () => {} : () => setGuestData(null)}
            onCreateLink={handleCreateLink}
            isError={IsErrors}
            errorMsg={errors}
            hasPassAssigningCheckbox={guestData.game_id}
          />
        )}

        <Alert
          isOpen={!!lotteryRequest}
          title="Teilnahme"
          message="An Lotterie für Ticket teilnehmen?"
          errorMessages={accessRequestError}
          positiveLabel="Ja"
          negativeLabel="Nein"
          onPositiveClick={() => accessRequest({ game_id: lotteryRequest.gameId, pass_id: lotteryRequest.passId })}
          onNegativeClick={handleLotteryRequestClose}
          onRequestClose={handleLotteryRequestClose}
          isLoading={isAccessRequestLoading}
        />

        {shareCard && (
          <ParkingShareModal
            card={shareCard}
            contacts={contacts && contacts.data ? contacts.data : null}
            onClose={() => setShareCard(null)}
          />
        )}
      </Container>
    </Page>
  );
}
